import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronRight, ChevronDown, Plus, Trash2, Send, RefreshCw, GripVertical, ArrowLeft } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { Project, InvoiceDetailLevel, RawEntry, InvoiceEntriesResponse } from '@/types/api'

// ─── Local types ──────────────────────────────────────────────────────────────

interface DesignerRow {
  rowId:         string
  description:   string
  qty:           number
  unitPrice:     number
  articleNumber: string
  entryIds:      number[]
  isCustom:      boolean
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function avgRate(rates: number[]): number {
  if (!rates.length) return 0
  return rates.reduce((s, r) => s + r, 0) / rates.length
}

function weekOf(dateStr: string): string {
  const d   = new Date(dateStr)
  const day = d.getDay()
  const mon = new Date(d)
  mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1))
  return mon.toISOString().slice(0, 10)
}

function autoGroup(entries: RawEntry[], detailLevel: InvoiceDetailLevel): DesignerRow[] {
  const groups = new Map<string, { qty: number; rates: number[]; desc: string; art: string; ids: number[] }>()

  for (const e of entries) {
    // Activity is the discriminator on new entries; legacy entries fall back
    // to article_number. Description text follows the same precedence.
    const disc = e.activityName ?? e.articleNumber ?? 'unspecified'
    const baseDesc = e.activityName ?? e.articleDescription ?? e.articleNumber ?? 'Time'
    let key: string
    let desc: string

    if (detailLevel === 'summary') {
      key  = disc
      desc = baseDesc
    } else if (detailLevel === 'per_phase') {
      const phase = e.phaseName ?? '—'
      key  = `${disc}::${phase}`
      desc = `${phase} — ${baseDesc}`
    } else if (detailLevel === 'per_consultant') {
      const staff = e.staffName ?? 'Unknown'
      key  = `${disc}::${staff}`
      desc = `${staff} — ${baseDesc}`
    } else if (detailLevel === 'per_week') {
      const staff = e.staffName ?? 'Unknown'
      const wk    = weekOf(e.entryDate)
      key  = `${disc}::${staff}::${wk}`
      desc = `${staff} — w/c ${wk}`
    } else if (detailLevel === 'per_day') {
      const staff = e.staffName ?? 'Unknown'
      key  = `${disc}::${staff}::${e.entryDate}`
      desc = `${staff} — ${e.entryDate}`
      if (e.invoiceText) desc += `: ${e.invoiceText}`
    } else {
      // per_entry
      key  = `entry::${e.id}`
      const text = e.invoiceText ?? baseDesc
      desc = `${e.staffName ?? 'Unknown'} — ${e.entryDate}: ${text}`
    }

    if (!groups.has(key)) {
      groups.set(key, { qty: 0, rates: [], desc, art: e.articleNumber ?? '', ids: [] })
    }
    const g = groups.get(key)!
    g.qty += e.billableHrs
    g.rates.push(e.rate)
    g.ids.push(e.id)
  }

  return Array.from(groups.entries()).map(([key, g]) => ({
    rowId:         key,
    description:   g.desc,
    qty:           Math.round(g.qty * 100) / 100,
    unitPrice:     Math.round(avgRate(g.rates) * 100) / 100,
    articleNumber: g.art,
    entryIds:      g.ids,
    isCustom:      false,
  }))
}

let _customRowCounter = 0

// ─── InvoiceComposerPage ──────────────────────────────────────────────────────

export default function InvoiceComposerPage() {
  const today      = new Date().toISOString().slice(0, 10)
  const monthStart = today.slice(0, 8) + '01'

  const [customerId,  setCustomerId]  = useState<number | null>(null)
  const [projectId,   setProjectId]   = useState<number | null>(null)
  const [from,        setFrom]        = useState(monthStart)
  const [to,          setTo]          = useState(today)
  const [rows,        setRows]        = useState<DesignerRow[]>([])
  const [entries,     setEntries]     = useState<RawEntry[]>([])
  const [expanded,    setExpanded]    = useState<Set<string>>(new Set())
  const [projectMeta, setProjectMeta] = useState<{ name: string; detailLevel: InvoiceDetailLevel } | null>(null)
  const [loading,     setLoading]     = useState(false)
  const [err,         setErr]         = useState<string | null>(null)
  const [dragging,    setDragging]    = useState<{ entryId: number; fromRowId: string } | null>(null)
  const [reordering,  setReordering]  = useState<string | null>(null)
  const [dragOver,    setDragOver]    = useState<string | null>(null)

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api.get<Project[]>('/projects'),
    staleTime: 5 * 60 * 1000,
  })
  const activeProjects = projects.filter(p => p.status === 'active')

  // Unique customers derived from active projects
  const customers = Array.from(
    new Map(
      activeProjects
        .filter(p => p.customerId !== null)
        .map(p => [p.customerId, { id: p.customerId!, name: p.customerName! }])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name))

  const filteredProjects = customerId
    ? activeProjects.filter(p => p.customerId === customerId)
    : activeProjects

  // Reset project when customer changes
  useEffect(() => { setProjectId(null) }, [customerId])

  // Reset designer when project or dates change
  useEffect(() => { setRows([]); setEntries([]); setProjectMeta(null); setErr(null) }, [projectId, from, to])

  const load = async () => {
    if (!projectId || !from || !to) return
    setLoading(true)
    setErr(null)
    try {
      const data = await api.get<InvoiceEntriesResponse>(
        `/projects/${projectId}/invoice-entries?from=${from}&to=${to}`
      )
      setEntries(data.entries)
      setProjectMeta({ name: data.projectName, detailLevel: data.detailLevel })
      setRows(autoGroup(data.entries, data.detailLevel))
      setExpanded(new Set())
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to load entries')
    } finally {
      setLoading(false)
    }
  }

  // ─── Row mutations ──────────────────────────────────────────────────────────

  const updateRow = (rowId: string, patch: Partial<DesignerRow>) =>
    setRows(prev => prev.map(r => r.rowId === rowId ? { ...r, ...patch } : r))

  const deleteRow = (rowId: string) =>
    setRows(prev => prev.filter(r => r.rowId !== rowId))

  const reorderRow = (fromRowId: string, toRowId: string) => {
    setRows(prev => {
      const from = prev.findIndex(r => r.rowId === fromRowId)
      const to   = prev.findIndex(r => r.rowId === toRowId)
      if (from === -1 || to === -1) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const moveEntry = (fromRowId: string, toRowId: string, entryId: number) => {
    if (fromRowId === toRowId) return
    const entry = entryById.get(entryId)
    if (!entry) return
    setRows(prev => prev.map(r => {
      if (r.rowId === fromRowId) return {
        ...r,
        entryIds: r.entryIds.filter(id => id !== entryId),
        qty: Math.max(0, Math.round((r.qty - entry.billableHrs) * 100) / 100),
      }
      if (r.rowId === toRowId) return {
        ...r,
        entryIds: [...r.entryIds, entryId],
        qty: Math.round((r.qty + entry.billableHrs) * 100) / 100,
      }
      return r
    }))
  }

  const removeEntry = (rowId: string, entryId: number) => {
    setRows(prev => prev.map(r => {
      if (r.rowId !== rowId) return r
      const entry = entryById.get(entryId)
      const newQty = Math.max(0, Math.round((r.qty - (entry?.billableHrs ?? 0)) * 100) / 100)
      return { ...r, entryIds: r.entryIds.filter(id => id !== entryId), qty: newQty }
    }))
  }

  const addCustomRow = () => {
    const rowId = `custom::${++_customRowCounter}`
    setRows(prev => [...prev, {
      rowId, description: '', qty: 0, unitPrice: 0,
      articleNumber: '', entryIds: [], isCustom: true,
    }])
  }

  const toggleExpand = (rowId: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(rowId) ? next.delete(rowId) : next.add(rowId)
      return next
    })

  // ─── Derived totals ─────────────────────────────────────────────────────────

  const totalQty    = rows.reduce((s, r) => s + r.qty, 0)
  const totalAmount = rows.reduce((s, r) => s + r.qty * r.unitPrice, 0)
  const hasMissingPrices = rows.some(r => r.unitPrice === 0 && r.qty > 0)

  const entryById = new Map(entries.map(e => [e.id, e]))

  const isLoaded = projectMeta !== null

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link
            to="/invoicing"
            className="row"
            style={{
              gap: 4, fontSize: 12, fontWeight: 500,
              color: 'var(--ns-text-muted)', marginBottom: 6,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft size={12} /> Back to Invoicing
          </Link>
          <div className="page-eyebrow">Invoicing</div>
          <h1 className="page-title">Invoice Composer</h1>
          <div className="page-subtitle">Build invoice rows from logged hours before sending to Fortnox</div>
        </div>
      </div>

      {/* Controls */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 200px' }}>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 5, color: 'var(--ns-text-muted)' }}>Customer</label>
            <select
              className="input"
              style={{ width: '100%' }}
              value={customerId ?? ''}
              onChange={e => setCustomerId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Select a customer…</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '2 1 200px' }}>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 5, color: 'var(--ns-text-muted)' }}>Project</label>
            <select
              className="input"
              style={{ width: '100%' }}
              value={projectId ?? ''}
              onChange={e => setProjectId(e.target.value ? Number(e.target.value) : null)}
              disabled={!customerId}
            >
              <option value="">{customerId ? 'Select a project…' : '— select customer first —'}</option>
              {filteredProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 5, color: 'var(--ns-text-muted)' }}>From</label>
            <input type="date" className="input" style={{ width: '100%' }} value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 5, color: 'var(--ns-text-muted)' }}>To</label>
            <input type="date" className="input" style={{ width: '100%' }} value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <button
            className="btn btn-brand"
            onClick={load}
            disabled={!projectId || !from || !to || loading}
            style={{ flexShrink: 0, gap: 6 }}
          >
            {loading ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />Loading…</> : isLoaded ? 'Reload' : 'Load entries'}
          </button>
        </div>
      </div>

      {err && (
        <div style={{ padding: '12px 16px', background: 'var(--ns-err-soft)', border: '1px solid var(--ns-err)', borderRadius: 8, fontSize: 13, color: 'var(--ns-err)' }}>
          {err}
        </div>
      )}

      {/* Empty state */}
      {!isLoaded && !loading && !err && (
        <div className="card muted" style={{ textAlign: 'center', padding: 60, fontSize: 13 }}>
          Select a project and date range, then click Load entries.
        </div>
      )}

      {isLoaded && entries.length === 0 && (
        <div className="card muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>
          No billable entries found for this project and period.
        </div>
      )}

      {isLoaded && entries.length > 0 && (
        <>
          {/* Summary bar */}
          <div className="card" style={{ padding: '14px 20px' }}>
            <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Project</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{projectMeta?.name}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Period</div>
                <div style={{ fontSize: 13 }}>{from} → {to}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Source entries</div>
                <div style={{ fontSize: 13 }}>{entries.length} entries → {rows.length} rows</div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Total hours</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{fmt(totalQty)} h</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Total amount</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{fmt(totalAmount)} kr</div>
              </div>
            </div>
          </div>

          {/* Composer table */}
          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <th>Description</th>
                  <th style={{ width: 90 }}>Article</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Hours</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Rate (kr)</th>
                  <th style={{ width: 120, textAlign: 'right' }}>Amount (kr)</th>
                  <th style={{ width: 32 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const amount    = row.qty * row.unitPrice
                  const isExpanded = expanded.has(row.rowId)
                  const sources   = row.entryIds.map(id => entryById.get(id)).filter(Boolean) as RawEntry[]

                  return (
                    <>
                      <tr
                        key={row.rowId}
                        draggable
                        onDragStart={e => {
                          if ((e.target as HTMLElement).closest('input,button,select')) { e.preventDefault(); return }
                          setReordering(row.rowId)
                        }}
                        onDragEnd={() => { setReordering(null); setDragOver(null) }}
                        onDragOver={e => { e.preventDefault(); if (reordering !== row.rowId && dragging?.fromRowId !== row.rowId) setDragOver(row.rowId) }}
                        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(null) }}
                        onDrop={e => {
                          e.preventDefault()
                          if (dragging && dragging.fromRowId !== row.rowId) moveEntry(dragging.fromRowId, row.rowId, dragging.entryId)
                          else if (reordering && reordering !== row.rowId) reorderRow(reordering, row.rowId)
                          setDragging(null); setReordering(null); setDragOver(null)
                        }}
                        style={{
                          opacity: reordering === row.rowId ? 0.4 : undefined,
                          background: dragOver === row.rowId
                            ? 'oklch(from var(--ns-brand-strong) l c h / 0.12)'
                            : isExpanded ? 'var(--ns-surface-2)' : undefined,
                          outline: dragOver === row.rowId ? '2px solid var(--ns-brand-strong)' : undefined,
                          outlineOffset: '-2px',
                          cursor: reordering === row.rowId ? 'grabbing' : undefined,
                        }}
                      >
                        {/* Grip + expand toggle */}
                        <td style={{ padding: '6px 2px 6px 8px', cursor: 'grab' }}>
                          <div className="row" style={{ gap: 2, alignItems: 'center' }}>
                            <GripVertical size={12} style={{ color: 'var(--ns-text-subtle)', flexShrink: 0 }} />
                            {sources.length > 0 && (
                              <button
                                className="btn btn-ghost btn-icon"
                                style={{ width: 18, height: 18, color: 'var(--ns-text-subtle)' }}
                                onClick={() => toggleExpand(row.rowId)}
                                title={isExpanded ? 'Collapse' : `Show ${sources.length} source entries`}
                              >
                                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </button>
                            )}
                          </div>
                        </td>

                        {/* Description */}
                        <td style={{ padding: '4px 6px' }}>
                          <input
                            className="input"
                            style={{ width: '100%', height: 30, fontSize: 13, padding: '0 8px', background: 'transparent', border: '1px solid transparent' }}
                            value={row.description}
                            placeholder="Invoice row description…"
                            onChange={e => updateRow(row.rowId, { description: e.target.value })}
                            onFocus={e  => (e.currentTarget.style.borderColor = 'var(--ns-brand-strong)')}
                            onBlur={e   => (e.currentTarget.style.borderColor = 'transparent')}
                          />
                        </td>

                        {/* Article */}
                        <td className="mono muted" style={{ fontSize: 12 }}>
                          {row.isCustom
                            ? <input
                                className="input mono"
                                style={{ width: '100%', height: 30, fontSize: 12, padding: '0 6px', background: 'transparent', border: '1px solid transparent' }}
                                value={row.articleNumber}
                                placeholder="ART-#"
                                onChange={e => updateRow(row.rowId, { articleNumber: e.target.value })}
                                onFocus={e  => (e.currentTarget.style.borderColor = 'var(--ns-brand-strong)')}
                                onBlur={e   => (e.currentTarget.style.borderColor = 'transparent')}
                              />
                            : row.articleNumber
                          }
                        </td>

                        {/* Hours / qty */}
                        <td style={{ padding: '4px 6px' }}>
                          <input
                            className="input mono tabular"
                            type="number"
                            min="0"
                            step="0.25"
                            style={{ width: '100%', height: 30, fontSize: 13, textAlign: 'right', padding: '0 8px', background: 'transparent', border: '1px solid transparent' }}
                            value={row.qty || ''}
                            placeholder="0"
                            onChange={e => updateRow(row.rowId, { qty: parseFloat(e.target.value) || 0 })}
                            onFocus={e  => (e.currentTarget.style.borderColor = 'var(--ns-brand-strong)')}
                            onBlur={e   => (e.currentTarget.style.borderColor = 'transparent')}
                          />
                        </td>

                        {/* Unit price */}
                        <td style={{ padding: '4px 6px' }}>
                          <input
                            className="input mono tabular"
                            type="number"
                            min="0"
                            step="50"
                            style={{ width: '100%', height: 30, fontSize: 13, textAlign: 'right', padding: '0 8px', background: 'transparent', border: '1px solid transparent' }}
                            value={row.unitPrice || ''}
                            placeholder="0"
                            onChange={e => updateRow(row.rowId, { unitPrice: parseFloat(e.target.value) || 0 })}
                            onFocus={e  => (e.currentTarget.style.borderColor = 'var(--ns-brand-strong)')}
                            onBlur={e   => (e.currentTarget.style.borderColor = 'transparent')}
                          />
                        </td>

                        {/* Amount */}
                        <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 600, fontSize: 13, paddingRight: 12 }}>
                          {row.unitPrice > 0
                            ? fmt(amount)
                            : <span className="muted" style={{ fontStyle: 'italic', fontWeight: 400 }}>—</span>
                          }
                        </td>

                        {/* Delete */}
                        <td>
                          <button
                            className="btn btn-ghost btn-icon"
                            style={{ color: 'var(--ns-text-subtle)' }}
                            onClick={() => deleteRow(row.rowId)}
                            title="Remove row"
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>

                      {/* Source entries (expanded) */}
                      {isExpanded && sources.map(e => (
                        <tr
                          key={`src-${e.id}`}
                          draggable
                          onDragStart={() => setDragging({ entryId: e.id, fromRowId: row.rowId })}
                          onDragEnd={() => { setDragging(null); setDragOver(null) }}
                          style={{
                            background: 'var(--ns-surface-3)',
                            opacity: dragging?.entryId === e.id ? 0.4 : undefined,
                            cursor: 'grab',
                          }}
                        >
                          <td style={{ paddingLeft: 10 }}>
                            <GripVertical size={12} style={{ color: 'var(--ns-text-subtle)', display: 'block' }} />
                          </td>
                          <td colSpan={2} style={{ paddingLeft: 28, fontSize: 12, color: 'var(--ns-text-muted)' }}>
                            <div className="row" style={{ gap: 10 }}>
                              <span className="mono">{e.entryDate}</span>
                              {e.staffName   && <span>{e.staffName}</span>}
                              {e.phaseName   && <span className="muted">· {e.phaseName}</span>}
                              {e.invoiceText && <span className="muted">· {e.invoiceText}</span>}
                            </div>
                          </td>
                          <td className="mono tabular muted" style={{ textAlign: 'right', fontSize: 12 }}>{fmt(e.billableHrs)}</td>
                          <td className="mono tabular muted" style={{ textAlign: 'right', fontSize: 12 }}>{e.rate > 0 ? fmt(e.rate) : '—'}</td>
                          <td className="mono tabular muted" style={{ textAlign: 'right', fontSize: 12, paddingRight: 12 }}>
                            {e.rate > 0 ? fmt(e.billableHrs * e.rate) : '—'}
                          </td>
                          <td>
                            <button
                              className="btn btn-ghost btn-icon"
                              style={{ color: 'var(--ns-text-subtle)', opacity: 0.6 }}
                              onClick={() => removeEntry(row.rowId, e.id)}
                              title="Remove this entry from the row"
                            >
                              <Trash2 size={11} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </>
                  )
                })}

                {/* Footer */}
                <tr style={{ background: 'var(--ns-surface-2)' }}>
                  <td colSpan={3} style={{ paddingLeft: 12 }}>
                    <button className="btn btn-ghost btn-sm" style={{ gap: 5 }} onClick={addCustomRow}>
                      <Plus size={12} />Add row
                    </button>
                  </td>
                  <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{fmt(totalQty)}</td>
                  <td />
                  <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 700, fontSize: 14, paddingRight: 12 }}>{fmt(totalAmount)} kr</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          {/* Bottom action bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {hasMissingPrices && (
                <span style={{ color: 'var(--ns-warn)' }}>
                  Some rows have no rate — set article prices or phase rate overrides in Settings.
                </span>
              )}
            </div>
            <button
              className="btn btn-brand"
              disabled
              style={{ gap: 6, opacity: 0.5, cursor: 'not-allowed' }}
              title="Fortnox write integration coming soon"
            >
              <Send size={13} />
              Push to Fortnox
            </button>
          </div>
        </>
      )}
    </div>
  )
}
